import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { patientApi, patientErr } from "@/lib/patientApi";
import { api, API } from "@/lib/api";
import { Storefront, CaretLeft, Package, Truck, Money } from "@phosphor-icons/react";
import AddressPicker from "@/components/AddressPicker";
import { readCart, cartSubtotal, removeFromCart, clearCart } from "@/lib/shopCart";

const rp = (n) => (n === null || n === undefined ? "—" : "Rp " + (Number(n) || 0).toLocaleString("id-ID"));
const inC = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#DB2777] focus:ring-2 focus:ring-[#DB2777]/15";
const coordOf = (a) => (a && a.latitude != null && a.longitude != null ? { latitude: a.latitude, longitude: a.longitude } : null);
const INSTANT_TXT = {
  coord_missing_destination: "Layanan instan (Grab/Gojek) memerlukan titik koordinat tujuan. Alamat ini belum memuat koordinat.",
  coord_missing_origin: "Layanan instan belum tersedia: titik koordinat lokasi GBS belum lengkap.",
  no_instant_courier_in_area: "Grab/Gojek belum tersedia untuk lokasi ini.",
  provider_error: "Gagal memeriksa kurir instan. Kurir reguler tetap tersedia; silakan coba lagi.",
};

function newIdemKey() {
  const k = sessionStorage.getItem("shop_idem");
  if (k) return k;
  const v = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
  sessionStorage.setItem("shop_idem", v);
  return v;
}

export default function ShopCheckout() {
  const nav = useNavigate();
  const [items, setItems] = useState(readCart());
  const [ftype, setFtype] = useState("pickup");
  const [name, setName] = useState("");
  const [wa, setWa] = useState("");
  const [addr, setAddr] = useState({});
  const [catatan, setCatatan] = useState("");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [areaResults, setAreaResults] = useState([]);
  const [destArea, setDestArea] = useState(null);
  const [rateOptions, setRateOptions] = useState(null);
  const [chosenSvc, setChosenSvc] = useState(null);
  const [rateBusy, setRateBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [instantInfo, setInstantInfo] = useState(null);
  const idemKey = useMemo(newIdemKey, []);

  useEffect(() => {
    document.title = "Checkout — HalloGBS";
    if (!localStorage.getItem("patient_token")) {
      nav("/pasien/masuk?returnTo=/checkout-belanja");
      return;
    }
    const sync = () => setItems(readCart());
    window.addEventListener("shop-cart-changed", sync);
    return () => window.removeEventListener("shop-cart-changed", sync);
  }, [nav]);

  // Subtotal DIHITUNG ULANG server (termasuk diskon). Jangan percaya harga localStorage.
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    api.post("/public/shop/quote", { items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })) })
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [items]);

  const lineOf = (it) => (quote?.lines || []).find((l) => l.product_id === it.product_id && l.variant_id === it.variant_id);
  const subtotal = quote ? quote.subtotal : cartSubtotal();
  const shippingLabel = ftype === "pickup" ? rp(0) : (chosenSvc ? rp(chosenSvc.price) : "Pilih layanan pengiriman");
  const total = ftype === "pickup" ? subtotal : (chosenSvc ? subtotal + chosenSvc.price : null);

  // Ganti area/keranjang/metode -> wajib pilih ulang layanan (quote lama tak dipercaya).
  useEffect(() => { setChosenSvc(null); setRateOptions(null); }, [destArea, items, ftype]);

  // ALAMAT = SATU SUMBER DATA. Area tujuan Biteship diturunkan dari Kode Pos alamat (tanpa input duplikat).
  useEffect(() => {
    if (ftype !== "delivery") { setDestArea(null); setAreaResults([]); return; }
    const pos = (addr.kode_pos || "").trim();
    setDestArea(null); setAreaResults([]); setRateOptions(null); setChosenSvc(null); setInstantInfo(null);
    if (pos.length < 5) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await patientApi.get("/patient/shop/shipping/areas", { params: { input: pos } });
        if (ignore) return;
        const list = data.areas || [];
        if (list.length === 1) setDestArea({ id: list[0].id, name: list[0].name, coord: coordOf(list[0]) });
        else setAreaResults(list);
      } catch (e) { if (!ignore) setAreaResults([]); }
    })();
    return () => { ignore = true; };
  }, [addr.kode_pos, ftype]);

  const destCoord = (() => {
    const ad = addr.alamat_data || {};
    if (ad.latitude != null && ad.longitude != null) return { latitude: ad.latitude, longitude: ad.longitude };
    return destArea && destArea.coord ? destArea.coord : null;
  })();

  const getRates = async () => {
    if (!destArea) return toast.error("Tujuan belum lengkap dari alamat");
    setRateBusy(true); setRateOptions(null); setChosenSvc(null); setInstantInfo(null);
    try {
      const { data } = await patientApi.post("/patient/shop/shipping/rates", {
        destination_area_id: destArea.id,
        destination_coord: destCoord || undefined,
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
      });
      setRateOptions(data.options || []);
      setInstantInfo({ available: !!data.instant_available, reason: data.instant_reason });
      if (!(data.options || []).length) toast.warning("Tidak ada layanan kurir untuk tujuan ini.");
    } catch (e) { setInstantInfo({ available: false, reason: "provider_error" }); toast.error(patientErr(e)); } finally { setRateBusy(false); }
  };

  const submit = async () => {
    if (items.length === 0) return toast.error("Keranjang kosong");
    if (!name.trim()) return toast.error("Nama penerima wajib diisi");
    if (!wa.trim()) return toast.error("Nomor WhatsApp wajib diisi");
    if (ftype === "delivery" && (!addr.alamat || !addr.alamat_data?.kelurahan)) return toast.error("Lengkapi alamat pengiriman");
    if (ftype === "delivery" && !destArea) return toast.error("Pilih area tujuan pengiriman");
    if (ftype === "delivery" && !chosenSvc) return toast.error("Pilih layanan pengiriman");
    if (ftype === "delivery" && !consent) return toast.error("Centang persetujuan berbagi data pengiriman");
    setLoading(true);
    const body = {
      idempotency_key: idemKey,
      fulfillment_type: ftype,
      recipient_name: name.trim(),
      recipient_wa: wa.trim(),
      items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
    };
    if (ftype === "delivery") {
      body.address = { alamat: addr.alamat, alamat_data: addr.alamat_data, kode_pos: addr.kode_pos, catatan };
      body.delivery_consent = true;
      body.shipping = {
        destination_area_id: destArea.id,
        destination_area_name: destArea.name,
        destination_coord: destCoord || undefined,
        courier_code: chosenSvc.courier_code,
        courier_service_code: chosenSvc.courier_service_code,
      };
    }
    try {
      const { data } = await patientApi.post("/patient/shop/checkout", body);
      (data.ordered || []).forEach((o) => removeFromCart(o.product_id, o.variant_id));
      sessionStorage.removeItem("shop_idem");
      if ((data.rejected || []).length) toast.warning(`${data.rejected.length} item tidak bisa dipesan (stok/nonaktif) dan tetap di keranjang.`);
      else toast.success("Pesanan dibuat");
      nav(`/pesanan-belanja/${data.order.order_no}`);
    } catch (e) { toast.error(patientErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="shop-checkout-page">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-pink-100 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/belanja/keranjang" data-testid="checkout-back" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={18} weight="bold" /> Keranjang</Link>
        <h1 className="font-heading text-lg font-extrabold text-[#4C1D95]">Checkout</h1>
        <span className="w-16" />
      </header>

      <div className="mx-auto max-w-2xl space-y-4 px-4 py-5 pb-28">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-pink-100 bg-white p-10 text-center text-sm text-slate-400" data-testid="checkout-empty">Keranjang kosong. <Link to="/belanja" className="font-bold text-[#7C4DE0]">Belanja dulu</Link></div>
        ) : (
          <>
            <section className="rounded-2xl border border-pink-100 bg-white p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Metode Pemenuhan</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="ftype-pickup" onClick={() => setFtype("pickup")} className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-sm font-bold transition-colors ${ftype === "pickup" ? "border-[#7C4DE0] bg-[#F5F0FF] text-[#6A38C7]" : "border-pink-100 text-slate-600"}`}><Package size={22} weight="duotone" /> Ambil di GBS</button>
                <button data-testid="ftype-delivery" onClick={() => setFtype("delivery")} className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-sm font-bold transition-colors ${ftype === "delivery" ? "border-[#7C4DE0] bg-[#F5F0FF] text-[#6A38C7]" : "border-pink-100 text-slate-600"}`}><Truck size={22} weight="duotone" /> Dikirim</button>
              </div>
            </section>

            <section className="rounded-2xl border border-pink-100 bg-white p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Data Penerima</p>
              <div className="space-y-3">
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nama Penerima *</label><input data-testid="checkout-name" value={name} onChange={(e) => setName(e.target.value)} className={inC} placeholder="Nama lengkap penerima" /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nomor WhatsApp *</label><input data-testid="checkout-wa" value={wa} onChange={(e) => setWa(e.target.value)} className={inC} placeholder="cth. 0812xxxxxxx" inputMode="tel" /></div>
              </div>
            </section>

            {ftype === "delivery" && (
              <section className="rounded-2xl border border-pink-100 bg-white p-4" data-testid="checkout-address-section">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Alamat Pengiriman</p>
                <AddressPicker value={addr} onPatch={(p) => setAddr((prev) => ({ ...prev, ...p }))} testidPrefix="checkout-addr" enableGeo />
                <div className="mt-3"><label className="mb-1 block text-xs font-semibold text-slate-500">Catatan Alamat (opsional)</label><textarea data-testid="checkout-catatan" rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} className={inC} placeholder="cth. patokan rumah, warna pagar" /></div>
              </section>
            )}

            {ftype === "delivery" && (
              <section className="rounded-2xl border border-pink-100 bg-white p-4" data-testid="checkout-shipping-section">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Pengiriman (Ongkir Otomatis)</p>
                {(!addr.kode_pos || (addr.kode_pos || "").trim().length < 5) ? (
                  <p data-testid="checkout-need-address" className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Lengkapi <b>Kode Pos</b> pada Alamat Pengiriman di atas untuk menghitung ongkir otomatis.</p>
                ) : destArea ? (
                  <div data-testid="checkout-dest-derived" className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">Tujuan: <b>{destArea.name}</b> <span className="text-slate-400">(otomatis dari alamat)</span></div>
                ) : areaResults.length > 1 ? (
                  <div data-testid="checkout-dest-choices">
                    <p className="mb-1 text-xs text-slate-500">Beberapa kecamatan cocok dengan kode pos ini—pilih sesuai alamat Anda:</p>
                    <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200">
                      {areaResults.map((a) => (
                        <button key={a.id} type="button" data-testid={`checkout-dest-opt-${a.id}`} onClick={() => setDestArea({ id: a.id, name: a.name, coord: coordOf(a) })} className="block w-full px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-pink-50">{a.name}</button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p data-testid="checkout-dest-none" className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Area tujuan tidak ditemukan dari Kode Pos. Periksa kembali Kode Pos pada Alamat Pengiriman di atas.</p>
                )}
                {destArea && (
                  <button data-testid="checkout-check-rates" type="button" onClick={getRates} disabled={rateBusy} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#7C4DE0] py-2.5 text-sm font-bold text-[#6A38C7] transition-colors hover:bg-[#F5F0FF] disabled:opacity-50">
                    <Truck size={18} weight="duotone" /> {rateBusy ? "Mengecek ongkir…" : "Cek Ongkir"}
                  </button>
                )}
                {rateOptions && rateOptions.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="checkout-rate-options">
                    {rateOptions.map((o, i) => {
                      const sel = chosenSvc && chosenSvc.courier_code === o.courier_code && chosenSvc.courier_service_code === o.courier_service_code;
                      return (
                        <button key={`${o.courier_code}-${o.courier_service_code}-${i}`} type="button" data-testid={`checkout-rate-${o.courier_code}-${o.courier_service_code}`} onClick={() => setChosenSvc(o)} className={`flex w-full items-center justify-between gap-2 rounded-xl border-2 p-3 text-left transition-colors ${sel ? "border-[#7C4DE0] bg-[#F5F0FF]" : "border-pink-100 hover:border-pink-200"}`}>
                          <span className="min-w-0"><span className="block text-sm font-bold text-slate-800">{o.courier_name} · {o.courier_service_name}</span><span className="block text-xs text-slate-400">Estimasi {o.duration || "—"}</span></span>
                          <span className="shrink-0 text-sm font-extrabold text-[#DB2777]">{rp(o.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {ftype === "delivery" && instantInfo && (
                  instantInfo.available ? (
                    <p data-testid="checkout-instant-status" className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">✓ Kurir instan (Grab/Gojek) tersedia untuk lokasi ini.</p>
                  ) : (
                    <p data-testid="checkout-instant-status" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">{INSTANT_TXT[instantInfo.reason] || "Grab/Gojek belum tersedia untuk lokasi ini."}</p>
                  )
                )}
              </section>
            )}

            <section className="rounded-2xl border border-pink-100 bg-white p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Ringkasan Pesanan</p>
              <div className="space-y-2">
                {items.map((it) => { const ln = lineOf(it); const unit = ln ? ln.price_final : it.price; const disc = ln && ln.discount_amount > 0;
                  return (
                  <div key={`${it.product_id}-${it.variant_id}`} data-testid={`checkout-item-${it.variant_id}`} className="flex justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1"><span className="font-semibold text-slate-800">{it.name}</span><span className="text-slate-400"> · {it.variant_label} × {it.qty}</span>{disc && <span className="ml-1 rounded-full bg-[#FCE7F3] px-1 text-[9px] font-extrabold text-[#DB2777]">-{ln.percent_off}%</span>}</span>
                    <span className="shrink-0 font-bold text-slate-700">{rp(unit * it.qty)}</span>
                  </div>
                ); })}
              </div>
              <div className="mt-3 space-y-1 border-t border-pink-50 pt-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span data-testid="checkout-subtotal" className="font-bold">{rp(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Ongkir</span><span data-testid="checkout-shipping" className="font-bold">{shippingLabel}</span></div>
                <div className="flex justify-between text-base"><span className="font-bold text-[#1E1B4B]">Total</span><span data-testid="checkout-total" className="font-extrabold text-[#DB2777]">{total === null ? "Menunggu ongkir" : rp(total)}</span></div>
              </div>
              {ftype === "delivery" && !chosenSvc && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Pilih area tujuan lalu Cek Ongkir untuk melihat layanan & tarif kurir. Pembayaran tersedia setelah layanan dipilih.</p>}
              {ftype === "delivery" && (
                <label data-testid="checkout-consent" className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <input data-testid="checkout-consent-check" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                  <span>Saya setuju nama, nomor kontak, dan alamat saya diteruskan ke mitra ekspedisi untuk pemrosesan pengiriman.</span>
                </label>
              )}
            </section>
          </>
        )}
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-pink-100 bg-white/95 px-4 py-3 backdrop-blur">
          <button data-testid="checkout-submit" onClick={submit} disabled={loading} className="mx-auto flex w-full max-w-2xl items-center justify-center gap-2 rounded-2xl bg-[#7C4DE0] py-3.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#6A38C7] active:scale-[0.99] disabled:opacity-50">
            <Money size={18} weight="fill" /> {loading ? "Memproses…" : "Buat Pesanan"}
          </button>
        </div>
      )}
    </div>
  );
}
